import React, { useState, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallData } from "../../contexts/CallDataContext";
import { useWeek } from "../../contexts/WeekContext";
import { useCustomer } from "../../contexts/CustomerContext";
import { startOfDay, endOfDay, isWithinInterval, format, subHours, subDays, startOfWeek } from "date-fns";
import { parseCustomTimestamp } from "../../utils/dateUtils";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const TokenMetrics = () => {
    const { data: callData } = useCallData();
    const { dateRange } = useWeek();
    const { availableCustomers } = useCustomer();

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        filters: false,
        kpis: false,
        charts: false,
        table: false
    });

    // Filter states
    const [filters, setFilters] = useState({
        selectedClient: null,
        selectedHour: null,
        groupBy: 'hour',
        tokenRange: { min: null, max: null },
        apiCallsFilter: null
    });

    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [expandedRows, setExpandedRows] = useState([]);

    // Reset pagination when filters or search change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters, searchQuery]);

    // Calculate token metrics from call data
    const tokenData = useMemo(() => {
        if (!callData || callData.length === 0) return [];

        const rangeStart = startOfDay(dateRange?.start || new Date());
        const rangeEnd = endOfDay(dateRange?.end || new Date());

        return callData
            .filter(call => {
                const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
                if (!callDate || !isWithinInterval(callDate, { start: rangeStart, end: rangeEnd })) {
                    return false;
                }

                // Apply client filter
                if (filters.selectedClient && call.metadata?.customer_id !== filters.selectedClient) {
                    return false;
                }

                // Apply hour filter
                if (filters.selectedHour !== null) {
                    const callHour = new Date(callDate).getHours();
                    if (callHour !== filters.selectedHour) {
                        return false;
                    }
                }

                // Apply token range filter
                if (filters.tokenRange.min !== null && call.token_usage?.total_tokens < filters.tokenRange.min) {
                    return false;
                }
                if (filters.tokenRange.max !== null && call.token_usage?.total_tokens > filters.tokenRange.max) {
                    return false;
                }

                // Apply API calls filter
                if (filters.apiCallsFilter !== null && call.token_usage?.api_calls !== filters.apiCallsFilter) {
                    return false;
                }

                return call.token_usage?.total_tokens > 0;
            })
            .map(call => {
                const tokenUsage = call.token_usage || {};
                const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
                const firstCall = tokenUsage.first_call ? new Date(tokenUsage.first_call) : callDate;
                const lastCall = tokenUsage.last_call ? new Date(tokenUsage.last_call) : callDate;
                const duration = lastCall - firstCall;

                return {
                    id: call.metadata?.stream_sid || call.id,
                    timestamp: callDate,
                    client: call.metadata?.customer_id || 'Unknown',
                    sessionId: call.metadata?.stream_sid || call.metadata?.session_id || 'N/A',
                    promptTokens: tokenUsage.prompt_tokens || 0,
                    completionTokens: tokenUsage.completion_tokens || 0,
                    totalTokens: tokenUsage.total_tokens || 0,
                    apiCalls: tokenUsage.api_calls || 1,
                    duration: duration,
                    tokenRatio: tokenUsage.completion_tokens && tokenUsage.prompt_tokens 
                        ? (tokenUsage.completion_tokens / tokenUsage.prompt_tokens).toFixed(2)
                        : 'N/A',
                    firstCall: firstCall,
                    lastCall: lastCall,
                    rawCall: call
                };
            });
    }, [callData, dateRange, filters]);

    // Calculate summary metrics
    const summaryMetrics = useMemo(() => {
        if (tokenData.length === 0) {
            return {
                totalTokens: 0,
                totalPromptTokens: 0,
                totalCompletionTokens: 0,
                avgTokensPerCall: 0,
                totalApiCalls: 0,
                peakHour: null,
                peakHourTokens: 0
            };
        }

        const totalTokens = tokenData.reduce((sum, item) => sum + item.totalTokens, 0);
        const totalPromptTokens = tokenData.reduce((sum, item) => sum + item.promptTokens, 0);
        const totalCompletionTokens = tokenData.reduce((sum, item) => sum + item.completionTokens, 0);
        const totalApiCalls = tokenData.reduce((sum, item) => sum + item.apiCalls, 0);
        const avgTokensPerCall = totalTokens / tokenData.length;

        // Find peak hour
        const hourlyUsage = {};
        tokenData.forEach(item => {
            const hour = new Date(item.timestamp).getHours();
            hourlyUsage[hour] = (hourlyUsage[hour] || 0) + item.totalTokens;
        });

        let peakHour = null;
        let peakHourTokens = 0;
        Object.entries(hourlyUsage).forEach(([hour, tokens]) => {
            if (tokens > peakHourTokens) {
                peakHourTokens = tokens;
                peakHour = parseInt(hour);
            }
        });

        return {
            totalTokens,
            totalPromptTokens,
            totalCompletionTokens,
            avgTokensPerCall: Math.round(avgTokensPerCall),
            totalApiCalls,
            peakHour,
            peakHourTokens
        };
    }, [tokenData]);

    // Group data for time series chart
    const timeSeriesData = useMemo(() => {
        if (tokenData.length === 0) return [];

        const grouped = {};
        tokenData.forEach(item => {
            let key;
            let displayTime;
            const date = new Date(item.timestamp);
            
            // If a specific hour is selected, group by 10-minute intervals
            if (filters.selectedHour !== null) {
                const minutes = date.getMinutes();
                const roundedMinutes = Math.floor(minutes / 10) * 10;
                const dateStr = format(date, 'yyyy-MM-dd');
                const hourStr = String(filters.selectedHour).padStart(2, '0');
                const minuteStr = String(roundedMinutes).padStart(2, '0');
                key = `${dateStr} ${hourStr}:${minuteStr}`;
                displayTime = `${hourStr}:${minuteStr}`;
            } else if (filters.groupBy === 'hour') {
                key = format(date, 'yyyy-MM-dd HH:00');
                displayTime = format(date, 'MM/dd HH:00');
            } else if (filters.groupBy === 'day') {
                key = format(date, 'yyyy-MM-dd');
                displayTime = format(date, 'MM/dd');
            } else if (filters.groupBy === 'week') {
                const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Monday as start of week
                key = format(weekStart, 'yyyy-MM-dd');
                displayTime = format(weekStart, 'MM/dd');
            } else {
                key = format(date, 'yyyy-MM');
                displayTime = format(date, 'MMM yyyy');
            }

            if (!grouped[key]) {
                grouped[key] = {
                    time: displayTime || key,
                    sortKey: key,
                    totalTokens: 0,
                    promptTokens: 0,
                    completionTokens: 0,
                    apiCalls: 0
                };
            }

            grouped[key].totalTokens += item.totalTokens;
            grouped[key].promptTokens += item.promptTokens;
            grouped[key].completionTokens += item.completionTokens;
            grouped[key].apiCalls += item.apiCalls;
        });

        return Object.values(grouped).sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    }, [tokenData, filters.groupBy, filters.selectedHour]);

    // Filter and sort table data
    const filteredTableData = useMemo(() => {
        let filtered = [...tokenData];

        // Apply search
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(item =>
                item.client.toLowerCase().includes(query) ||
                item.sessionId.toLowerCase().includes(query) ||
                item.totalTokens.toString().includes(query)
            );
        }

        // Apply sorting
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];
                
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;
                
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                }
                
                const aStr = String(aVal);
                const bStr = String(bVal);
                return sortConfig.direction === 'asc' 
                    ? aStr.localeCompare(bStr)
                    : bStr.localeCompare(aStr);
            });
        }

        return filtered;
    }, [tokenData, searchQuery, sortConfig]);

    // Paginate table data
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;
        const end = start + rowsPerPage;
        return filteredTableData.slice(start, end);
    }, [filteredTableData, currentPage, rowsPerPage]);

    // Pie chart data
    const pieChartData = useMemo(() => {
        return [
            { name: 'Prompt Tokens', value: summaryMetrics.totalPromptTokens },
            { name: 'Completion Tokens', value: summaryMetrics.totalCompletionTokens }
        ];
    }, [summaryMetrics]);

    const COLORS = ['#3B82F6', '#10B981'];

    // Animation effects
    useEffect(() => {
        const timers = [
            setTimeout(() => setAnimationStates(prev => ({ ...prev, header: true })), 100),
            setTimeout(() => setAnimationStates(prev => ({ ...prev, filters: true })), 200),
            setTimeout(() => setAnimationStates(prev => ({ ...prev, kpis: true })), 300),
            setTimeout(() => setAnimationStates(prev => ({ ...prev, charts: true })), 400),
            setTimeout(() => setAnimationStates(prev => ({ ...prev, table: true })), 500)
        ];
        return () => timers.forEach(clearTimeout);
    }, []);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const toggleRowExpansion = (id) => {
        setExpandedRows(prev => 
            prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
        );
    };

    const formatDuration = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    };

    const formatHour = (hour) => {
        if (hour === null) return 'N/A';
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour} ${period}`;
    };

    return (
        <div className="responsive-page-layout scrollable gap-6">
            {/* Header */}
            <div className={`transition-all duration-700 ease-out ${
                animationStates.header ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}>
                <div className="header-textbox">
                    <Icon icon="material-symbols:token-outline" className="header-icon" />
                    Token Metrics
                </div>
            </div>

            {/* Filters Bar */}
            <div className={`w-full flex flex-wrap gap-2 transition-all duration-500 ease-out ${
                animationStates.filters ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
                {/* <select
                    value={filters.selectedClient || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, selectedClient: e.target.value || null }))}
                    className="px-3 py-2 bg-bgSecondary border border-border rounded-md text-sm"
                >
                    <option value="">All Clients</option>
                    {availableCustomers.map(customer => (
                        <option key={customer.id} value={customer.id}>{customer.name}</option>
                    ))}
                </select> */}

                <select
                    value={filters.groupBy}
                    onChange={(e) => setFilters(prev => ({ ...prev, groupBy: e.target.value }))}
                    disabled={filters.selectedHour !== null}
                    className={`px-3 py-2 bg-bgSecondary border border-border rounded-md text-sm ${
                        filters.selectedHour !== null ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                    <option value="hour">Group by Hour</option>
                    <option value="day">Group by Day</option>
                    <option value="week">Group by Week</option>
                    <option value="month">Group by Month</option>
                </select>

                <select
                    value={filters.selectedHour !== null ? filters.selectedHour : ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, selectedHour: e.target.value !== '' ? parseInt(e.target.value) : null }))}
                    className="px-3 py-2 bg-bgSecondary border border-border rounded-md text-sm"
                >
                    <option value="">All Hours</option>
                    {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{formatHour(i)}</option>
                    ))}
                </select>

                <input
                    type="number"
                    placeholder="Min Tokens"
                    value={filters.tokenRange.min || ''}
                    onChange={(e) => setFilters(prev => ({
                        ...prev,
                        tokenRange: { ...prev.tokenRange, min: e.target.value ? parseInt(e.target.value) : null }
                    }))}
                    className="px-3 py-2 bg-bgSecondary border border-border rounded-md text-sm w-32"
                />

                <input
                    type="number"
                    placeholder="Max Tokens"
                    value={filters.tokenRange.max || ''}
                    onChange={(e) => setFilters(prev => ({
                        ...prev,
                        tokenRange: { ...prev.tokenRange, max: e.target.value ? parseInt(e.target.value) : null }
                    }))}
                    className="px-3 py-2 bg-bgSecondary border border-border rounded-md text-sm w-32"
                />

                <button
                    onClick={() => setFilters({
                        selectedClient: null,
                        selectedHour: null,
                        groupBy: 'hour',
                        tokenRange: { min: null, max: null },
                        apiCallsFilter: null
                    })}
                    className="px-3 py-2 button-container rounded-md text-sm flex items-center gap-2"
                >
                    <Icon icon="material-symbols:refresh" className="text-xl" />
                    Reset Filters
                </button>
            </div>

            {/* KPI Cards */}
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full transition-all duration-500 ease-out ${
                animationStates.kpis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
                <div className="card-container rounded-md p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Icon icon="material-symbols:token-outline" className="text-2xl text-primary" />
                        <span className="text-sm text-font-secondary">Total Tokens Used</span>
                    </div>
                    <div className="text-2xl font-bold text-font-primary">
                        {summaryMetrics.totalTokens.toLocaleString()}
                    </div>
                    <div className="text-xs text-font-secondary mt-1">in selected period</div>
                </div>

                <div className="card-container rounded-md p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Icon icon="material-symbols:input-circle" className="text-2xl text-blue-500" />
                        <span className="text-sm text-font-secondary">Total Prompt Tokens</span>
                    </div>
                    <div className="text-2xl font-bold text-font-primary">
                        {summaryMetrics.totalPromptTokens.toLocaleString()}
                    </div>
                    <div className="text-xs text-font-secondary mt-1">
                        {summaryMetrics.totalTokens > 0 
                            ? `${((summaryMetrics.totalPromptTokens / summaryMetrics.totalTokens) * 100).toFixed(1)}% of total`
                            : 'Input tokens'}
                    </div>
                </div>

                <div className="card-container rounded-md p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Icon icon="material-symbols:output-circle" className="text-2xl text-green-500" />
                        <span className="text-sm text-font-secondary">Total Completion Tokens</span>
                    </div>
                    <div className="text-2xl font-bold text-font-primary">
                        {summaryMetrics.totalCompletionTokens.toLocaleString()}
                    </div>
                    <div className="text-xs text-font-secondary mt-1">
                        {summaryMetrics.totalTokens > 0
                            ? `${((summaryMetrics.totalCompletionTokens / summaryMetrics.totalTokens) * 100).toFixed(1)}% of total (more expensive)`
                            : 'Output tokens'}
                    </div>
                </div>

                <div className="card-container rounded-md p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Icon icon="hugeicons:chart-average" className="text-2xl text-purple-500" />
                        <span className="text-sm text-font-secondary">Average Tokens Per Call</span>
                    </div>
                    <div className="text-2xl font-bold text-font-primary">
                        {summaryMetrics.avgTokensPerCall.toLocaleString()}
                    </div>
                    <div className="text-xs text-font-secondary mt-1">efficiency metric</div>
                </div>

                <div className="card-container rounded-md p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Icon icon="material-symbols:api" className="text-2xl text-orange-500" />
                        <span className="text-sm text-font-secondary">Total API Calls</span>
                    </div>
                    <div className="text-2xl font-bold text-font-primary">
                        {summaryMetrics.totalApiCalls.toLocaleString()}
                    </div>
                    <div className="text-xs text-font-secondary mt-1">number of LLM requests</div>
                </div>

                <div className="card-container rounded-md p-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Icon icon="material-symbols:schedule" className="text-2xl text-yellow-500" />
                        <span className="text-sm text-font-secondary">Peak Hour</span>
                    </div>
                    <div className="text-2xl font-bold text-font-primary">
                        {formatHour(summaryMetrics.peakHour)}
                    </div>
                    <div className="text-xs text-font-secondary mt-1">
                        {summaryMetrics.peakHourTokens.toLocaleString()} tokens
                    </div>
                </div>
            </div>

            {/* Charts Row */}
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-4 w-full transition-all duration-500 ease-out ${
                animationStates.charts ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
                {/* Time Series Chart */}
                <div className="lg:col-span-2 card-container rounded-md p-4">
                    <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center mb-2">
                        <div className="text-base xl:text-lg font-semibold text-font-primary">Token Usage Over Time</div>
                        <div className="flex flex-row items-center gap-2 md:gap-3">
                            <div className="flex items-center gap-1 text-sm text-font-secondary">
                                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#10B981' }}></div>
                                <span>Prompt Tokens (Y-Axis Left)</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm text-font-secondary">
                                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#F59E0B' }}></div>
                                <span>Completion Tokens (Y-Axis Right)</span>
                            </div>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={timeSeriesData} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} vertical={false} />
                            <XAxis 
                                dataKey="time" 
                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis 
                                yAxisId="left"
                                orientation="left"
                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                stroke="#10B981"
                            />
                            <YAxis 
                                yAxisId="right"
                                orientation="right"
                                tick={{ fill: '#6B7280', fontSize: 12 }}
                                axisLine={false}
                                tickLine={false}
                                stroke="#F59E0B"
                            />
                            <Tooltip 
                                contentStyle={{ backgroundColor: '#F9FAFB', borderRadius: '4px', border: 'none' }}
                            />
                            <Legend 
                                verticalAlign="top"
                                height={0}
                                content={() => null}
                            />
                            <Line 
                                yAxisId="left"
                                type="monotone" 
                                dataKey="promptTokens" 
                                stroke="#10B981" 
                                strokeWidth={2}
                                name="Prompt Tokens"
                                dot={false}
                            />
                            <Line 
                                yAxisId="right"
                                type="monotone" 
                                dataKey="completionTokens" 
                                stroke="#F59E0B" 
                                strokeWidth={2}
                                name="Completion Tokens"
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Pie Chart */}
                {/* <div className="card-container rounded-md p-4">
                    <div className="text-base font-semibold text-font-primary mb-4">Token Distribution</div>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={pieChartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {pieChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div> */}
            </div>

            {/* Token Usage Logs Table */}
            <div className={`w-full transition-all duration-500 ease-out ${
                animationStates.table ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}>
                <div className="card-container rounded-md p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                        <div className="text-base font-semibold text-font-primary">Token Usage Logs</div>
                        <div className="flex gap-2 w-full sm:w-auto">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="px-3 py-2 bg-bgSecondary border border-border rounded-md text-sm flex-1 sm:w-64"
                            />
                            <select
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value));
                                    setCurrentPage(1);
                                }}
                                className="px-3 py-2 bg-bgSecondary border border-border rounded-md text-sm"
                            >
                                <option value={25}>25 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </select>
                        </div>
                    </div>

                    {paginatedData.length === 0 ? (
                        <div className="text-center py-8 text-font-secondary">
                            <Icon icon="material-symbols:search-off" className="text-4xl mb-2" />
                            <p>No token usage data found matching your filters.</p>
                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-bgAccent text-font-primary font-semibold">
                                            <th 
                                                className="text-left py-2 px-3 cursor-pointer hover:text-primary"
                                                onClick={() => handleSort('timestamp')}
                                            >
                                                Timestamp {sortConfig.key === 'timestamp' && (
                                                    <Icon icon={sortConfig.direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} className="inline ml-1" />
                                                )}
                                            </th>
                                            <th className="text-left py-2 px-3">Session ID</th>
                                            <th 
                                                className="text-left py-2 px-3 cursor-pointer hover:text-primary"
                                                onClick={() => handleSort('promptTokens')}
                                            >
                                                Prompt {sortConfig.key === 'promptTokens' && (
                                                    <Icon icon={sortConfig.direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} className="inline ml-1" />
                                                )}
                                            </th>
                                            <th 
                                                className="text-left py-2 px-3 cursor-pointer hover:text-primary"
                                                onClick={() => handleSort('completionTokens')}
                                            >
                                                Completion {sortConfig.key === 'completionTokens' && (
                                                    <Icon icon={sortConfig.direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} className="inline ml-1" />
                                                )}
                                            </th>
                                            <th 
                                                className="text-left py-2 px-3 cursor-pointer hover:text-primary font-bold"
                                                onClick={() => handleSort('totalTokens')}
                                            >
                                                Total {sortConfig.key === 'totalTokens' && (
                                                    <Icon icon={sortConfig.direction === 'asc' ? 'mdi:arrow-up' : 'mdi:arrow-down'} className="inline ml-1" />
                                                )}
                                            </th>
                                            <th className="text-left py-2 px-3">API Calls</th>
                                            <th className="text-left py-2 px-3">Duration</th>
                                            <th className="text-left py-2 px-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedData.map((item, index) => {
                                            const rowId = `${item.id}-${index}`;
                                            const isExpanded = expandedRows.includes(rowId);
                                            return (
                                                <React.Fragment key={rowId}>
                                                    <tr 
                                                        className="border-b border-bgAccent text-font-secondary hover:bg-bgSecondary"
                                                    >
                                                        <td className="py-2 px-3">
                                                            {format(new Date(item.timestamp), 'MM/dd/yyyy HH:mm')}
                                                        </td>
                                                        <td className="py-2 px-3 font-mono text-xs">{item.sessionId}</td>
                                                        <td className="py-2 px-3">{item.promptTokens.toLocaleString()}</td>
                                                        <td className="py-2 px-3">{item.completionTokens.toLocaleString()}</td>
                                                        <td className="py-2 px-3 font-bold text-primary">
                                                            {item.totalTokens.toLocaleString()}
                                                        </td>
                                                        <td className="py-2 px-3">{item.apiCalls}</td>
                                                        <td className="py-2 px-3">{formatDuration(item.duration)}</td>
                                                        <td 
                                                            className="py-2 px-3 cursor-pointer hover:text-primary"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRowExpansion(rowId);
                                                            }}
                                                        >
                                                            <Icon 
                                                                icon={isExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} 
                                                                className="text-lg"
                                                            />
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr className="bg-bgSecondary" key={`${rowId}-expanded`}>
                                                            <td colSpan={8} className="py-4 px-3">
                                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                                    <div>
                                                                        <span className="text-font-secondary">First Call:</span>
                                                                        <span className="text-font-primary ml-2">
                                                                            {format(new Date(item.firstCall), 'MM/dd/yyyy HH:mm:ss')}
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-font-secondary">Last Call:</span>
                                                                        <span className="text-font-primary ml-2">
                                                                            {format(new Date(item.lastCall), 'MM/dd/yyyy HH:mm:ss')}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            <div className="flex justify-between items-center mt-4">
                                <div className="text-sm text-font-secondary">
                                    Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredTableData.length)} of {filteredTableData.length} entries
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 button-container rounded-md text-sm disabled:opacity-50"
                                    >
                                        Previous
                                    </button>
                                    <span className="px-3 py-1 text-sm text-font-primary">
                                        Page {currentPage} of {Math.ceil(filteredTableData.length / rowsPerPage)}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(Math.ceil(filteredTableData.length / rowsPerPage), prev + 1))}
                                        disabled={currentPage >= Math.ceil(filteredTableData.length / rowsPerPage)}
                                        className="px-3 py-1 button-container rounded-md text-sm disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TokenMetrics;

